import {
  Action,
  ActionGroup,
  Autocomplete,
  Button,
  CodeBlock,
  Content,
  ContextualHelp,
  ContextualHelpTrigger,
  FieldDescription,
  FieldError,
  Header,
  Heading,
  Icon,
  Label,
  Modal,
  Option,
  Section,
  Select,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Text,
  TextField,
} from "@mittwald/flow-remote-react-components";
import { IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  ALL_MIME_TYPES,
  CATEGORIES,
  buildDefaultCachingConfig,
  isKnownMimeType,
  MIME_FORMAT,
  PRESET_LABELS,
  PRESETS,
  type CacheCustomRule,
  type CachePreset,
  type CachingConfig,
  type Category,
  type CategoryKey,
} from "../pageSpeedConfig.ts";

interface DraftRule extends CacheCustomRule {
  id: string;
}

interface Draft {
  categories: Record<CategoryKey, CachePreset>;
  customRules: DraftRule[];
}

let nextId = 0;
const makeId = (): string => `r${String(++nextId)}`;

const toDraft = (config: CachingConfig): Draft => ({
  categories: {
    static: config.static,
    dynamic: config.dynamic,
    feeds: config.feeds,
  },
  customRules: config.custom.map((rule) => ({ ...rule, id: makeId() })),
});

const fromDraft = (draft: Draft): CachingConfig => ({
  enabled: true,
  static: draft.categories.static,
  dynamic: draft.categories.dynamic,
  feeds: draft.categories.feeds,
  custom: draft.customRules.map((rule) => ({
    mimeType: rule.mimeType,
    preset: rule.preset,
  })),
});

const presetOptions = PRESETS.map((p) => (
  <Option key={p} value={p}>
    {PRESET_LABELS[p]}
  </Option>
));

const renderCategoryHelp = (c: Category) => (
  <ContextualHelpTrigger>
    <Button />
    <ContextualHelp>
      <Heading>{c.label}</Heading>
      <Text>{c.helpText}</Text>
      <CodeBlock language="text" code={c.mimes.join("\n")} />
    </ContextualHelp>
  </ContextualHelpTrigger>
);

const renderMimeSuggestions = (query: string) =>
  query.length >= 2
    ? ALL_MIME_TYPES.filter((m) =>
        m.toLowerCase().includes(query.toLowerCase()),
      )
        .slice(0, 50)
        .map((m) => (
          <Option key={m} value={m} textValue={m}>
            {m}
          </Option>
        ))
    : null;

interface PageSpeedCachingDialogProps {
  initial: CachingConfig;
  onSave: (next: CachingConfig) => void;
}

export const PageSpeedCachingDialog = ({
  initial,
  onSave,
}: PageSpeedCachingDialogProps) => {
  const [draft, setDraft] = useState<Draft>(() => toDraft(initial));
  const [autoFocusId, setAutoFocusId] = useState<string | null>(null);

  useEffect(() => {
    if (autoFocusId !== null) {
      const handle = requestAnimationFrame(() => {
        setAutoFocusId(null);
      });
      return () => {
        cancelAnimationFrame(handle);
      };
    }
  }, [autoFocusId]);

  const setCategory = (key: CategoryKey, preset: CachePreset) => {
    setDraft((d) => ({
      ...d,
      categories: { ...d.categories, [key]: preset },
    }));
  };

  const addRule = () => {
    const newId = makeId();
    setDraft((d) => ({
      ...d,
      customRules: [
        ...d.customRules,
        { id: newId, mimeType: "", preset: "1mo" },
      ],
    }));
    setAutoFocusId(newId);
  };

  const updateRule = (id: string, patch: Partial<CacheCustomRule>) => {
    setDraft((d) => ({
      ...d,
      customRules: d.customRules.map((r) =>
        r.id === id ? { ...r, ...patch } : r,
      ),
    }));
  };

  const removeRule = (id: string) => {
    setDraft((d) => ({
      ...d,
      customRules: d.customRules.filter((r) => r.id !== id),
    }));
  };

  const resetCategories = () => {
    const defaults = buildDefaultCachingConfig();
    setDraft((d) => ({
      ...d,
      categories: {
        static: defaults.static,
        dynamic: defaults.dynamic,
        feeds: defaults.feeds,
      },
    }));
  };

  const resetCustomRules = () => {
    setDraft((d) => ({ ...d, customRules: [] }));
  };

  const canSave = draft.customRules.every((r) => MIME_FORMAT.test(r.mimeType));
  const categoriesAreDefault = CATEGORIES.every(
    (c) => draft.categories[c.key] === c.default,
  );

  return (
    <Modal offCanvas>
      <Heading>Caching anpassen</Heading>
      <Content>
        <Section>
          <Header>
            <Heading>Dateityp-Gruppen</Heading>
            {!categoriesAreDefault && (
              <Button
                onPress={resetCategories}
                variant="soft"
                color="secondary"
                size="s"
              >
                Zurücksetzen
              </Button>
            )}
          </Header>
          {CATEGORIES.map((c) => (
            <Select
              key={c.key}
              isRequired
              value={draft.categories[c.key]}
              onChange={(k) => {
                if (k !== null) setCategory(c.key, k as CachePreset);
              }}
            >
              <Label>
                {c.label} {renderCategoryHelp(c)}
              </Label>
              {presetOptions}
            </Select>
          ))}
        </Section>

        <Section>
          <Header>
            <Heading>Eigene Regeln</Heading>
            {draft.customRules.length > 0 && (
              <Button
                onPress={resetCustomRules}
                variant="soft"
                color="secondary"
                size="s"
              >
                Zurücksetzen
              </Button>
            )}
            <Button onPress={addRule} color="success" size="s">
              Regel hinzufügen
            </Button>
          </Header>
          <Text>
            Für weitere MIME-Types. Regeln hier überschreiben die Gruppen oben,
            falls sich ein Typ überschneidet.
          </Text>
          {draft.customRules.length > 0 && (
            <Table aria-label="Eigene Regeln">
              <TableHeader>
                <TableColumn>MIME-Type</TableColumn>
                <TableColumn>Cache-Dauer</TableColumn>
                <TableColumn>Aktion</TableColumn>
              </TableHeader>
              <TableBody>
                {draft.customRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Autocomplete>
                        <TextField
                          autoFocus={rule.id === autoFocusId}
                          aria-label="MIME-Type"
                          value={rule.mimeType}
                          onChange={(v) => {
                            updateRule(rule.id, { mimeType: v });
                          }}
                          isInvalid={
                            rule.mimeType.length > 0 &&
                            !MIME_FORMAT.test(rule.mimeType)
                          }
                        >
                          <FieldError>
                            Ungültiges Format. Erwartet: type/subtype
                          </FieldError>
                          {rule.mimeType.length > 0 &&
                            MIME_FORMAT.test(rule.mimeType) &&
                            !isKnownMimeType(rule.mimeType) && (
                              <FieldDescription>
                                Nicht in der Liste gängiger MIME-Types.
                                Tippfehler?
                              </FieldDescription>
                            )}
                        </TextField>
                        {renderMimeSuggestions(rule.mimeType)}
                      </Autocomplete>
                    </TableCell>
                    <TableCell>
                      <Select
                        aria-label="Cache-Dauer"
                        isRequired
                        value={rule.preset}
                        onChange={(k) => {
                          if (k !== null)
                            updateRule(rule.id, { preset: k as CachePreset });
                        }}
                      >
                        {presetOptions}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        aria-label="Regel löschen"
                        color="danger"
                        variant="plain"
                        onPress={() => {
                          removeRule(rule.id);
                        }}
                      >
                        <Icon>
                          <IconTrash />
                        </Icon>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      </Content>

      <ActionGroup>
        <Action closeOverlay="Modal">
          <Action
            onAction={() => {
              onSave(fromDraft(draft));
            }}
          >
            <Button color="success" isDisabled={!canSave}>
              Speichern
            </Button>
          </Action>
          <Button color="secondary" variant="soft">
            Abbrechen
          </Button>
        </Action>
      </ActionGroup>
    </Modal>
  );
};
